import debounce from "@lodash/debounce"

class Accordion extends HTMLElement {
  static observedAttributes = [
    "data-open",
    "data-is-open",
    "data-animation-state",
    "data-is-fully-open",
    "data-is-fully-closed",
    "data-height",
    "style",
  ]

  constructor() {
    super()
    this.height = 0
    this.id = `accordion-${crypto.randomUUID()}`
  }

  connectedCallback() {
    this._collapsibleContainer.addEventListener(
      "transitionend",
      this.handleTransitionEnd.bind(this)
    )
    this._collapsibleContainer.setAttribute("id", this.id)
    this._trigger.setAttribute("aria-controls", this.id)
    this._trigger.setAttribute("aria-expanded", this.open)
    this._collapsibleContainer.setAttribute(
      "style",
      Object.entries(this.collapsibleContainerStyles)
        .map(([k, v]) => `${k}:${v}`)
        .join(";")
    )
    // Set height if the accordion is already open
    if (this.open || this._collapsibleContainer) {
      this.height = this._collapsibleContainer.scrollHeight
    }
  }

  get open() {
    return this.dataset.open == "true" ? true : false
  }
  set open(value) {
    this.setAttribute("data-open", `${value}`)
  }
  get height() {
    return parseInt(this.dataset.height)
  }
  set height(value) {
    this.setAttribute("data-height", `${value}`)
  }
  get isOpen() {
    return this.dataset.isOpen == "true" ? true : false
  }
  set isOpen(value) {
    this.setAttribute("data-is-open", `${value}`)
  }
  get animationState() {
    return this.dataset.animationState
  }
  set animationState(value) {
    this.setAttribute("data-animation-state", `${value}`)
  }
  get isFullyOpen() {
    return this.animationState === "idle" && this.open && this.isOpen
  }
  get isFullyClosed() {
    return this.animationState === "idle" && !this.open && !this.isOpen
  }
  get collapsibleContainerStyles() {
    return {
      ...(this.isFullyClosed ? { display: "none" } : {}),
      "max-height": this.isFullyOpen ? "none" : `${this.height}px`,
      opacity: this.height ? 1 : 0,
      overflow: this.isFullyOpen ? "auto" : "hidden",
    }
  }

  get _collapsibleContainer() {
    return this.querySelector(".accordion__content__collapsible_wrapper")
  }

  get _trigger() {
    return this.querySelector(".accordion__title")
  }

  handleIsFullyOpen = () => {
    this.setAttribute(
      "data-is-fully-open",
      `${this.animationState === "idle" && this.open && this.isOpen}`
    )
  }

  handleIsFullyClosed = () => {
    this.setAttribute(
      "data-is-fully-closed",
      `${this.animationState === "idle" && !this.open && !this.isOpen}`
    )
  }

  attributeChangedCallback(name, prev, curr) {
    if (prev === null) return
    switch (name) {
      case "data-open": {
        curr = curr == "true" ? true : false
        if (curr !== this.isOpen) {
          this.animationState = "measuring"
        }
        this.handleIsFullyOpen()
        this.handleIsFullyClosed()
        // Update aria-expanded for accessibility
        this._trigger.setAttribute("aria-expanded", curr)
        break
      }
      case "data-is-open": {
        curr = curr == "true" ? true : false
        if (curr !== this.open) {
          this.animationState = "measuring"
        }
        this.handleIsFullyOpen()
        this.handleIsFullyClosed()
        break
      }
      case "data-animation-state": {
        this.animateHeight()
        this.handleIsFullyOpen()
        this.handleIsFullyClosed()
        break
      }
      case "data-height":
      case "data-is-fully-closed":
      case "data-is-fully-open": {
        this._collapsibleContainer.setAttribute(
          "style",
          Object.entries(this.collapsibleContainerStyles)
            .map(([k, v]) => `${k}:${v}`)
            .join(";")
        )
        break
      }
      default:
        break
    }
  }

  closeOthers = debounce(
    () => {
      if (this.animationState == "animating") {
        return
      }
      this.dispatchEvent(
        new CustomEvent("accordion:closeOthers", {
          bubbles: true,
          composed: true,
        })
      )
    },
    332,
    { leading: true, trailing: true }
  )

  animateHeight() {
    if (!this._collapsibleContainer) return

    setTimeout(() => {
      switch (this.animationState) {
        case "idle":
          break
        case "measuring":
          this.height = this._collapsibleContainer.scrollHeight
          this.animationState = "animating"
          break
        case "animating": {
          this.height = this.open ? this._collapsibleContainer.scrollHeight : 0
          break
        }
      }
    }, 0)
  }

  handleTransitionEnd({ target }) {
    if (target === this._collapsibleContainer) {
      this.animationState = "idle"
      this.isOpen = this.open
    }
  }
}

customElements.define("component-accordion", Accordion)
